import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '../hosts';
import { QueryEntity } from '../types';
import { MatterSpeakers } from '../types/backend-dto-types';
import useGetFetchConfig, { FetchMethod } from '../useGetFetchConfig';

interface RenameMatterSpeakerParams {
  speakerId: string;
  name: string;
}

/**
 * Rename a matter speaker
 */
export const useRenameMatterSpeaker = (matterId: string | undefined) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  const renameSpeaker = async ({ speakerId, name }: RenameMatterSpeakerParams): Promise<MatterSpeakers[number]> => {
    const fetchConfig = getFetchConfig({ method: FetchMethod.PATCH, data: { name } });

    const response = await fetch(`${APIBaseChronos}/api/matters/${matterId}/audio/speakers/${speakerId}`, fetchConfig);
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { message?: string } | null;
      throw new Error(body?.message ?? 'Renaming speaker failed');
    }
    return response.json();
  };

  return useMutation({
    mutationFn: renameSpeaker,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries([QueryEntity.MatterAudioSpeakers]),
        queryClient.invalidateQueries([QueryEntity.DocumentAudioBlocks]),
      ]);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
